import React, { useEffect, useState, useCallback } from 'react';
import Modal from 'react-modal';
import { supabase } from '../lib/supabaseClient';
import { MathJaxContext } from 'better-react-mathjax';
import { renderClarifyBlocks } from '../components/renderClarifyBlocks';

const mathJaxConfig = {
  loader: { load: ['input/tex', 'output/chtml'] },
  tex: {
    inlineMath: [['$', '$'], ['\\(', '\\)']],
    displayMath: [['\\[', '\\]']]
  }
};

export default function ClarifyModalView({ clarifyId, isOpen, onClose }) {
  const [entries, setEntries] = useState([]);
  const [shouldTypeset, setShouldTypeset] = useState(false);

  const handleClose = useCallback(() => {
    // Reset state before closing
    setEntries([]);
    setShouldTypeset(false);
    
    // Clean up MathJax elements if needed
    if (window.MathJax?.typesetClear) {
      window.MathJax.typesetClear();
    }
    
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!clarifyId || !isOpen) return;

    const fetchClarifications = async () => {
      const { data, error } = await supabase
        .from('clarify_entries')
        .select('*')
        .eq('clarify_topic_id', clarifyId)
        .order('order_index');

      if (!error) {
        setEntries(data || []);
        setShouldTypeset(true);
      }
    };

    fetchClarifications();

    // Cleanup function
    return () => {
      setEntries([]);
      setShouldTypeset(false);
    };
  }, [clarifyId, isOpen]);

  useEffect(() => {
    if (!shouldTypeset || !isOpen) return;

    const waitAndTypeset = async () => {
      try {
        // Wait for DOM to be ready
        await new Promise((resolve) => setTimeout(resolve, 200));
        
        if (window.MathJax?.typesetPromise) {
          await window.MathJax.typesetPromise();
        }
      } catch (err) {
        console.warn('❌ MathJax typeset error:', err.message);
      } finally {
        setShouldTypeset(false);
      }
    };

    waitAndTypeset();
  }, [shouldTypeset, isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Clarify Modal"
      className="bg-white rounded shadow-lg max-w-2xl mx-auto mt-20 p-6 outline-none"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start overflow-y-auto"
      ariaHideApp={false}
    >
      <MathJaxContext config={mathJaxConfig}>
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-indigo-700">Clarification</h2>
            <button
              onClick={handleClose}
              className="text-sm text-red-500 hover:underline"
            >
              ✖ Close
            </button>
          </div>

          {entries.length > 0 ? (
            entries.map((entry, index) => (
              <div key={entry.id || index} className="mb-6">
                <h3 className="text-lg font-medium text-indigo-600 mb-2">{entry.title}</h3>
                <div className="space-y-3">{renderClarifyBlocks(entry.content_blocks)}</div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">Loading clarification...</p>
          )}
        </div>
      </MathJaxContext>
    </Modal>
  );
}